import {TaskCache} from "@shared/services/api/tickets/store/task-cache";
import {EquipmentService} from "@shared/services/api/equipment.service";
import {RulesService} from "@app/layout/rules/rulesmanager/rules.service";
import {AceLoggerService} from "@logging/ace-logger.service";
import {HttpUtilityService} from "@shared/services/api/http-utility-service";
import {map, mergeMap, Observable, of} from "rxjs";
import {AceLogger} from "@logging/ace-logger";
import {Equipment, EquipmentId, Level, Metadata, MetadataId, MetadataType, MetadataTypeId} from "@app/graphql-typings/graphql";
import {Task, TaskId} from "@shared/services/api/task.interface";
import {Injectable} from "@angular/core";
import {User} from '@app/layout/admin/services/api/user-interfaces';
import {CommonAlertCacheData, EscalatedTicket, TicketsService} from '@app/shared/services';
import {UsersCache} from '@app/shared/services/api/tickets/store/users-cache';
import {CacheById} from './cache-by-id';
import {TicketUtils} from "@app/layout/sites/tickets/ticket-utils";
import {CollectionUtils, IterableUtil, MapUtils, SetUtils} from "../util/collections/collection-utils";
import {MetadataTypeService} from "../services/api/metadata-type.service";
import {SiteId} from "../services/api/access-sites.interface";
import {Impact, Priority} from "@app/layout/rules/template-editor/api/template";
import {Template, TemplateId} from "@app/layout/rules/template-editor/api/template";
import {TemplatesService} from "@app/layout/rules/template-editor/services/templates.service";
import {MetadataService} from "../services/api/metadata.service";
import {AlertNotification, NotificationService, SiteNotificationId} from "../services/api/notification.service";
import {AlertNotificationByIdCache} from "./alert-notification/alert-notification-by-id-cache";
import {SiteNotificationIdsBySiteIdCache} from "./alert-notification/site-notification-ids-by-site-id-cache";
import {PriorityId} from "../api/model/type-ids";
import {CacheByParams} from "./cache-by-params";
import {TemplateCategory, TemplateCategoryId} from "@app/layout/rules/template-categories/models/template-category";
import {TemplateCategoriesService} from "@app/layout/rules/template-categories/template-categories.service";
import {LevelZoneService} from "@app/layout/sites/manage/manage-sites/upsert-site/forms/level-zone-form/level-zone.service";


export type TicketCacheImportData = CommonAlertCacheData;


@Injectable()
export class TicketDataCaches implements TicketCacheImportData {
  metadataTypesByIdCache: CacheById<MetadataTypeId, MetadataType>;
  equipmentCache: CacheById<EquipmentId, Equipment>;
  equipmentIdsBySiteIdCache: CacheById<SiteId, EquipmentId[]>;
  templatesBySiteIdCache: CacheById<SiteId, Template[]>;
  metadataByIdCache: CacheById<MetadataId, Metadata>;
  siteNotificationIdsBySiteIdCache: CacheById<number, number[]>;
  alertNotificationsByIdCache: CacheById<number, AlertNotification>;
  private readonly TICKET_FETCH_LIMIT = 1000;
  private readonly PARTITION_SIZE = 100;
  private readonly logger: AceLogger;
  private readonly assigneesCache: UsersCache;
  private readonly assigneesByEmailBySiteIdCache: CacheById<SiteId, Map<string, User>>;
  private readonly levelsBySiteIdCache: CacheById<SiteId, Level[]>;
  private readonly taskCache: TaskCache;
  private readonly ticketsByRuleIdCache: CacheById<TaskId, EscalatedTicket[]>;
  private readonly templateCategoriesByIdCache: CacheById<TemplateCategoryId, TemplateCategory>;
  private readonly allMetadataTypeIdCache: CacheByParams<[], MetadataTypeId[], string>;

  constructor(private httpUtilityService: HttpUtilityService,
              private ticketService: TicketsService,
              private templateService: TemplatesService,
              private metadataTypeService: MetadataTypeService,
              templateCategoriesService: TemplateCategoriesService,
              notificationService: NotificationService,
              equipmentService: EquipmentService,
              metadataService: MetadataService,
              rulesResolverService: RulesService,
              levelZoneService: LevelZoneService,
              logService: AceLoggerService) {
    this.logger = logService.create(TicketDataCaches.name);
    this.metadataTypesByIdCache = new CacheById(metadataTypeIds => metadataTypeService.getMetadataTypesForIds(metadataTypeIds)
      .pipe(map(metadataTypes => CollectionUtils.indexBy(metadataTypes, 'type_id'))));
    this.allMetadataTypeIdCache = new CacheByParams(
      () => this.metadataTypeService.getAllMetadataTypes().pipe(
        mergeMap(metadataTypes => {
          const observableMetadataTypesById = CollectionUtils.indexBy(metadataTypes, 'type_id', metadataType => of(metadataType));
          return this.metadataTypesByIdCache.setItems(observableMetadataTypesById).pipe(
            map(() => Array.from(observableMetadataTypesById.keys()))
          );
        })
      ),
      () => ''
    );
    this.equipmentCache = new CacheById(equipmentIds =>
      equipmentService.getEquipmentByIds(equipmentIds).pipe(map(equipments => CollectionUtils.indexBy(equipments, 'equipment_id'))));
    this.equipmentIdsBySiteIdCache = new CacheById(siteIds => equipmentService.getEquipmentByIdBySiteIds(siteIds, {includeLevelZone: true}).pipe(mergeMap(equipmentByIdBySiteId => {
      const observableEquipmentByIds = new Map<EquipmentId, Observable<Equipment>>();
      equipmentByIdBySiteId.forEach(equipmentByIds => CollectionUtils.indexIntoExistingBy(observableEquipmentByIds, equipmentByIds, 0, ([_id, equipment]) => of(equipment)));
      return this.equipmentCache.setItems(observableEquipmentByIds).pipe(map(() => {
        const siteIdAndEquipmentIdTuple = IterableUtil.map(equipmentByIdBySiteId, ([siteId, equipmentById]) => [siteId, Array.from(equipmentById.keys())] as const);
        return new Map(siteIdAndEquipmentIdTuple);
      }))
    })));
    this.assigneesCache = new UsersCache(ticketService);
    this.assigneesByEmailBySiteIdCache = new CacheById((siteIds: SiteId[]) => this.getAssigneesBySiteIds(siteIds).pipe(map(usersByIdBySiteId => {
      return IterableUtil.reduce(
        usersByIdBySiteId,
        (usersByEmailBySiteId, [siteId, usersById]) => usersByEmailBySiteId.set(siteId, CollectionUtils.indexBy(usersById.values(), 'email')),
        new Map<SiteId, Map<string, User>>()
      );
    })));
    this.taskCache = new TaskCache(rulesResolverService, logService);
    this.ticketsByRuleIdCache = new CacheById(ruleIds => this.getAllTicketsByRuleId(ruleIds));
    this.templatesBySiteIdCache = new CacheById((siteIds: SiteId[]) => templateService.getTemplatesBySiteIds(siteIds));
    this.levelsBySiteIdCache = new CacheById((siteIds: SiteId[]) =>
      levelZoneService.getLevelsBySiteIds(siteIds).pipe(map(levels => {
        const levelsBySiteId = CollectionUtils.groupBy(levels, 'site_id');
        return new Map(siteIds.map(siteId => [siteId, levelsBySiteId.get(siteId) ?? []]));
      })));
    this.templateCategoriesByIdCache = new CacheById(templateCategoryIds =>
      templateCategoriesService.getAllTemplateCategories({template_category_ids: templateCategoryIds})
        .pipe(map(categories => CollectionUtils.indexBy(categories, 'template_category_id'))));
    this.metadataByIdCache = new CacheById(ids => metadataService.getMetadataByIds(ids).pipe(map(metadataList => CollectionUtils.indexBy(metadataList, 'metadata_id'))));

    this.alertNotificationsByIdCache = new AlertNotificationByIdCache(
      httpUtilityService,
      notificationService,
      this.PARTITION_SIZE
    );
    this.siteNotificationIdsBySiteIdCache = new SiteNotificationIdsBySiteIdCache(
      httpUtilityService,
      notificationService,
      this.PARTITION_SIZE,
      this.alertNotificationsByIdCache
    );
  }

  public getEquipmentBySiteIds(siteIds: SiteId[]): Observable<Map<SiteId, Map<EquipmentId, Equipment>>> {
    return this.equipmentIdsBySiteIdCache.getByIds(siteIds).pipe(
      mergeMap(equipmentIdsBySiteIds => {
        const unifiedEquipmentIds = new Set<EquipmentId>;
        equipmentIdsBySiteIds.forEach(equipmentIds => SetUtils.addAll(unifiedEquipmentIds, equipmentIds));
        return this.equipmentCache.getByIds(Array.from(unifiedEquipmentIds)).pipe(
          map(equipmentByIds => {
            const siteIdAndEquipmentByIdTuple = IterableUtil.map(equipmentIdsBySiteIds, ([siteId, equipmentIds]) => {
              const siteEquipmentById = CollectionUtils.indexByFn(equipmentIds, id => id, id => equipmentByIds.get(id));
              return [siteId, siteEquipmentById] as const;
            });
            return new Map(siteIdAndEquipmentByIdTuple);
          })
        );
      })
    );
  }

  public getEquipmentBySiteId(siteId: SiteId): Observable<Map<EquipmentId, Equipment>> {
    return this.getEquipmentBySiteIds([siteId]).pipe(map(equipmentByIds => equipmentByIds.get(siteId)));
  }

  public getMetadataTypesByIdBySiteId(siteId: SiteId): Observable<Map<MetadataTypeId, MetadataType>> {
    return this.getEquipmentBySiteId(siteId).pipe(
      mergeMap(equipmentById => {
        const metadataTypeIds = new Set(IterableUtil.map(equipmentById.values(), equipment => equipment.metadata_type_id));
        return this.metadataTypesByIdCache.getByIds(Array.from(metadataTypeIds))
      })
    );
  }

  public getMetadataTypesByIds(ids: MetadataTypeId[]): Observable<Map<MetadataTypeId, MetadataType>> {
    return this.metadataTypesByIdCache.getByIds(ids);
  }

  public getAllMetadataTypes(): Observable<MetadataType[]> {
    return this.allMetadataTypeIdCache.get().pipe(
      mergeMap(metadataTypeIds => this.metadataTypesByIdCache.getByIds(metadataTypeIds)),
      map(metadataTypeByIds => Array.from(metadataTypeByIds.values()))
    );
  }

  public getTasksByIds(taskIds: number[]): Observable<Map<number, Task>> {
    return this.taskCache.getTasksByIds(taskIds);
  }

  public getAssigneesBySiteId(siteId: number): Observable<Map<string, User>> {
    return this.assigneesCache.getUsersForSites([siteId]).pipe(map(users => users.get(siteId) ?? new Map()));
  }

  public getAssigneesByEmailBySiteId(siteId: number): Observable<Map<string, User>> {
    return this.assigneesByEmailBySiteIdCache.getById(siteId);
  }

  public getLevelsBySiteId(siteId: SiteId): Observable<Level[]> {
    return this.levelsBySiteIdCache.getById(siteId);
  }

  public getAssigneesBySiteIds(siteIds: number[]): Observable<Map<number, Map<string, User>>> {
    return this.assigneesCache.getUsersForSites(siteIds);
  }

  public getTicketsByRuleIds(ruleIds: number[]): Observable<Map<TaskId, EscalatedTicket[]>> {
    return ruleIds?.length
      ? this.ticketsByRuleIdCache.getByIds(ruleIds)
      : of(null);
  }

  public getImpactsById(): Observable<Map<string, Impact>> {
    // already cached in template service
    return this.templateService.getImpacts().pipe(map(impacts => CollectionUtils.indexBy(impacts, 'impact_id')));
  }

  public getPrioritiesById(): Observable<Map<PriorityId, Priority>> {
    // already cached in template service
    return this.templateService.getPriorities().pipe(map(priorities => CollectionUtils.indexBy(priorities, 'priority_id')));
  }

  public getTemplateCategoriesByIds(ids: TemplateCategoryId[]): Observable<Map<TemplateCategoryId, TemplateCategory>> {
    return this.templateCategoriesByIdCache.getByIds(ids);
  }

  public watchTicketsByRuleIds(ruleIds: TaskId[]): Observable<Map<TaskId, EscalatedTicket[]>> {
    return this.ticketsByRuleIdCache.watch(ruleIds);
  }

  public getTemplatesByIdBySiteId(siteId: SiteId): Observable<Map<TemplateId, Template>> {
    return this.getTemplatesByIdBySiteIds([siteId]);
  }

  public getEquipmentByIds(equipmentIds: EquipmentId[]): Observable<Map<EquipmentId, Equipment>> {
    return this.equipmentCache.getByIds(equipmentIds);
  }

  public getTemplatesByIdBySiteIds(siteIds: SiteId[]): Observable<Map<TemplateId, Template>> {
    return this.templatesBySiteIdCache.getByIds(siteIds).pipe(
      map(templates => IterableUtil.reduce(
        templates.values(),
        (allTemplatesById, siteTemplates) => {
          CollectionUtils.indexIntoExistingBy(allTemplatesById, siteTemplates, 'template_id');
          return allTemplatesById;
        },
        new Map<TemplateId, Template>()
      ))
    );
  }

  public getMetadataByIdBySiteId(siteId: SiteId): Observable<Map<MetadataId, Metadata>> {
    return this.templatesBySiteIdCache.getById(siteId).pipe(mergeMap(templates => {
      const metadataIds: Set<MetadataId> = templates.reduce((metadataIds, template) => {
        template?.sensors?.forEach(sensor => metadataIds.add(sensor.metadata_id));
        return metadataIds;
      }, new Set<MetadataId>());
      return this.metadataByIdCache.getByIds(Array.from(metadataIds));
    }));
  }

  public updateRuleTicketCache(
    oldTaskIdsByTicketId: Map<string, number[]>,
    newTaskIdsByTicketId: Map<string, number[]>,
    escalatedTicketsToUpdateById: Map<string, EscalatedTicket>
  ) {
    const ticketIdsToRemoveByTaskIds = new Map<number, string[]>();
    const ticketIdsToAddByTaskIds = new Map<number, string[]>();
    escalatedTicketsToUpdateById.forEach((_ticket, ticketId) => {
      const oldTaskIds = new Set(oldTaskIdsByTicketId.get(ticketId));
      const newTaskIds = new Set(newTaskIdsByTicketId.get(ticketId));
      oldTaskIds.forEach(oldTaskId => {
        if (!newTaskIds.has(oldTaskId)) {
          MapUtils.setDefault(ticketIdsToRemoveByTaskIds, oldTaskId, []).push(ticketId);
        } else {
          newTaskIds.delete(oldTaskId);
        }
      });
      newTaskIds.forEach(newTaskId => MapUtils.setDefault(ticketIdsToAddByTaskIds, newTaskId, []).push(ticketId));
    });
    if (ticketIdsToRemoveByTaskIds.size || ticketIdsToAddByTaskIds.size) {
      const affectedTaskIds = new Set([...ticketIdsToRemoveByTaskIds.keys(), ...ticketIdsToAddByTaskIds.keys()]);
      this.ticketsByRuleIdCache.updateItemsIfExists(
        [...affectedTaskIds],
        (taskId, cachedTickets) => {
          const newTicketsById = CollectionUtils.indexBy(cachedTickets, 'ticket_id');
          ticketIdsToRemoveByTaskIds.get(taskId)?.forEach(id => newTicketsById.delete(id));
          ticketIdsToAddByTaskIds.get(taskId)?.forEach(id => newTicketsById.set(id, escalatedTicketsToUpdateById.get(id)));
          return of([...newTicketsById.values()]);
        }
      );
    }
  }

  public getAllAlertNotificationsBySiteId(siteId: SiteId): Observable<Map<SiteNotificationId, AlertNotification>> {
    return this.siteNotificationIdsBySiteIdCache.getById(siteId).pipe(mergeMap((siteNotificationIds) => this.alertNotificationsByIdCache.getByIds(siteNotificationIds)));
  }

  public exportData(): TicketCacheImportData {
    return {
      equipmentCache: this.equipmentCache,
      equipmentIdsBySiteIdCache: this.equipmentIdsBySiteIdCache,
      metadataTypesByIdCache: this.metadataTypesByIdCache,
      templatesBySiteIdCache: this.templatesBySiteIdCache,
      metadataByIdCache: this.metadataByIdCache,
      alertNotificationsByIdCache: this.alertNotificationsByIdCache,
      siteNotificationIdsBySiteIdCache: this.siteNotificationIdsBySiteIdCache
    };
  }

  public importData(importData: TicketCacheImportData) {
    if (importData?.equipmentCache) {
      this.equipmentCache = importData.equipmentCache;
    }
    if (importData?.equipmentIdsBySiteIdCache) {
      this.equipmentIdsBySiteIdCache = importData.equipmentIdsBySiteIdCache;
    }
    if (importData?.metadataTypesByIdCache) {
      this.metadataTypesByIdCache = importData.metadataTypesByIdCache;
    }
    if (importData?.templatesBySiteIdCache) {
      this.templatesBySiteIdCache = importData.templatesBySiteIdCache;
    }
    if (importData?.metadataByIdCache) {
      this.metadataByIdCache = importData.metadataByIdCache;
    }
    if (importData?.alertNotificationsByIdCache) {
      this.alertNotificationsByIdCache = importData.alertNotificationsByIdCache;
    }
    if (importData.siteNotificationIdsBySiteIdCache) {
      this.siteNotificationIdsBySiteIdCache = importData.siteNotificationIdsBySiteIdCache;
    }
  }

  private getAllTicketsByRuleId(ruleIds: number[]): Observable<Map<number, EscalatedTicket[]> | null> {
    return this.httpUtilityService.pageUntilComplete(
      (pageInfo) => this.ticketService.getEscalatedTickets(
        {rule_ids: ruleIds, ticket_archived: false},
        pageInfo.rows,
        pageInfo.first,
        null,
        {includeLinkedTickets: true}
      ),
      {first: 0, rows: this.TICKET_FETCH_LIMIT}
    ).pipe(map(tickets => {
      const requestedIdsSet = new Set(ruleIds);
      const ticketListByRuleId = new Map<number, EscalatedTicket[]>(ruleIds.map(ruleId => [ruleId, []]));
      tickets.forEach(ticket => {
        TicketUtils.getTaskIdsFromActions([ticket]).forEach(taskId => {
          if (requestedIdsSet.has(taskId)) {
            const ruleActions = ticketListByRuleId.get(taskId);
            ruleActions.push(ticket);
          }
        });
      });
      return ticketListByRuleId;
    }));
  }
}
